import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Alert, Box, ToggleButton, ToggleButtonGroup, useMediaQuery, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TuneIcon from '@mui/icons-material/Tune';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import FlowBuilder from '../components/FlowBuilder/FlowBuilder';
import AiBuilderPane from '../components/FlowBuilder/ai/AiBuilderPane';
import flowGenerationService from '../services/flowGenerationService';
import { compile } from '../flow-core/compile';
import { isBlankFlow } from '../flow-core/hydrate';
import { useFlow } from '../contexts/FlowContext';
import { useAuth } from '../contexts/AuthContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useWhatsAppConnection } from '../contexts/WhatsAppConnectionContext';
import { getTemplate } from '../data/flowTemplates';
import { exampleFor, DEFAULT_EXAMPLE_ID } from '../data/examples';
import { track } from '../utils/analytics';

/** Same cap the landing page applies before putting a brief in the URL. */
const MAX_BRIEF = 4000;

/** The search params minus one one-shot key; what remains (?mode=, ?screen=) stays in the URL. */
const without = (params, key) => {
  const rest = new URLSearchParams(params);
  rest.delete(key);
  return rest;
};

const FlowBuilderPage = () => {
  const { flowId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadFlowById, setCurrentFlow, createFlow, flows, hasLoadedFlows } = useFlow();
  const { authenticatedFetch } = useAuth();
  const { selectedEnvironment } = useEnvironment();
  const { isConnected } = useWhatsAppConnection();
  const templateId = searchParams.get('template');
  const isNewFlow = searchParams.get('new') === '1';
  // A brief typed on the landing page (or anywhere else that links in): the visitor described
  // the form before they had an account, and the builder is where that gets built. Read once,
  // stripped from the URL immediately so a refresh does not generate it a second time.
  const brief = searchParams.get('brief');
  // "Show me one", the landing page's Build button with an empty box. A flow we already have,
  // shown as the conversation that would have produced it: no wait, no cost, and every button
  // in the builder works on it from there.
  const example = searchParams.get('example');
  // One-shot guard: the template branch awaits an async save while ?template= is
  // still in the URL, so dependency churn must not re-trigger it.
  const handledTemplateRef = useRef(null);
  const handledNewRef = useRef(false);
  // Whether this visit asked for a blank page, captured at mount: the `new` param is stripped
  // from the URL as soon as it is handled, and the assistant pane reads its draft in an effect
  // that runs BEFORE this page's, so by the time the strip lands, the old conversation would
  // already be back on screen. The pane checks this instead of the URL.
  const startBlankRef = useRef(isNewFlow);

  // A new flow opens in the assistant; an existing one opens where its work already lives.
  // Both modes edit the SAME flow, so the toggle moves between two views of one thing rather
  // than between two documents.
  const theme = useTheme();
  // Same test the shell uses, so the two never disagree about what a phone is.
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
  const isPhone = isNarrow || isTouch;

  // Building by hand means a palette, a canvas and a property panel side by side. A phone has
  // room for one of the three, so the manual builder is not offered there, it was not a poor
  // experience, it was an unusable one.
  const manualAllowed = !isPhone;

  // The mode lives in the URL (?mode=assistant | manual) so a view can be linked to and comes
  // back on reload; the flows list's "Start from scratch" link is ?mode=manual. Without the
  // param, a new flow opens in the assistant and an existing one on the canvas.
  const requestedMode = searchParams.get('mode');
  const mode = requestedMode === 'manual'
    ? 'manual'
    : (requestedMode === 'assistant' || requestedMode === 'ai')
      ? 'ai'
      : (isNewFlow || !flowId ? 'ai' : 'manual');
  const setMode = useCallback((next) => {
    if (!next) return;
    const params = new URLSearchParams(searchParams);
    params.set('mode', next === 'ai' ? 'assistant' : 'manual');
    // Replace, not push: flipping between the two views is not a place Back should stop at.
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  const [aiAvailable, setAiAvailable] = useState(false);
  // The canvas as it stands, reported up by the builder. Read live rather than from the saved
  // flow so the assistant is never handed a version the user has already moved on from.
  const screensRef = useRef(null);

  useEffect(() => {
    if (!selectedEnvironment?.id) return;
    // Hide the mode entirely when the server has no API key, rather than offering a tab that
    // can only fail.
    flowGenerationService.isAvailable(selectedEnvironment.id, authenticatedFetch)
      .then(setAiAvailable);
  }, [selectedEnvironment?.id, authenticatedFetch]);

  useEffect(() => {
    // Covers opening an existing flow (which starts in manual) and rotating or resizing into
    // phone territory mid-session.
    if (!manualAllowed && aiAvailable && mode !== 'ai') {
      setMode('ai');
    }
  }, [manualAllowed, aiAvailable, mode, setMode]);

  const currentFlowJson = useCallback(() => {
    try {
      const screens = screensRef.current;
      if (!screens || screens.length === 0) return null;
      const flowJson = compile(screens);
      // A blank canvas compiles to a real one-screen flow, so without this the assistant
      // believes a flow already exists: it skips its own empty state and offers to edit a
      // placeholder screen the author never wrote.
      return isBlankFlow(flowJson) ? null : flowJson;
    } catch {
      // A canvas mid-edit can be momentarily uncompilable. Sending nothing is right here: the
      // assistant then starts fresh rather than from a flow that does not exist.
      return null;
    }
  }, []);

  const handleScreensChange = useCallback((screens) => {
    screensRef.current = screens;
  }, []);

  /**
   * @param name what to call it, when the caller knows better than the first screen's title
   *   does, the worked example has a name of its own, and which effect runs first should not
   *   decide whether the flow is "Barber shop bookings" or "Book a cut".
   */
  const applyGenerated = useCallback((flowJson, name) => {
    // Null means "there is no flow here any more", the assistant clearing the canvas before it
    // builds something unrelated to what was on it. Keeping the old record with a null flowJson
    // would leave its name on screen and the old screens on the canvas.
    if (!flowJson) {
      setCurrentFlow(null);
      return;
    }
    setCurrentFlow((previous) => ({
      ...(previous || {}),
      name: name || previous?.name || flowJson?.screens?.[0]?.title || 'Untitled flow',
      flowJson,
    }));
  }, [setCurrentFlow]);

  useEffect(() => {
    // Param gone (stripped or navigated away), allow the same template to be
    // picked again later; the guard only needs to absorb re-runs while
    // ?template= is still in the URL.
    if (!templateId) {
      handledTemplateRef.current = null;
    }
    if (!isNewFlow) {
      handledNewRef.current = false;
    }

    if (flowId) {
      // The environment must be loaded before a flow can be resolved; on a
      // page refresh of a deep link it isn't ready on the first run, so wait
      // for it rather than treating the flow as missing.
      if (!selectedEnvironment?.id) {
        return;
      }

      loadFlowById(flowId).then(flow => {
        if (!flow) {
          // Flow genuinely not found, redirect to a fresh flow builder.
          navigate('/flow-builder?new=1', { replace: true });
        }
      });
      return;
    }

    // One-shot params: ?template=<id> loads a template, ?new=1 resets to a
    // blank slate. Both strip themselves after acting so a refresh doesn't
    // clobber the user's edits. Crucially, a bare /flow-builder (incl. the
    // post-strip re-render, and effect re-runs caused by context identities
    // changing, e.g. the environment loading in) does NOTHING: the previous
    // implementation's "blank-slate fallthrough" wiped the freshly loaded
    // template the moment any dependency re-fired, which is why templates
    // appeared to create blank flows.
    if (templateId) {
      // The auto-save below needs the environment's flow list (name-collision
      // check) and an environment to save into, wait for both rather than
      // acting on incomplete data. The ?template= param stays in the URL, so
      // the effect re-runs when they arrive.
      if (!selectedEnvironment?.id || !hasLoadedFlows) {
        return;
      }
      if (handledTemplateRef.current === templateId) {
        return;
      }
      handledTemplateRef.current = templateId;

      // Arriving with a template chosen (the landing page's "make this one yours") is a first
      // run of its own, the flows list must not redirect over it a moment later.
      sessionStorage.setItem('fg_first_run_done', '1');
      const template = getTemplate(templateId);
      // Opened FROM a template, so the assistant is where the work continues: "tell me what to
      // change". Without pinning the mode here the flow gets an id a moment later, and an
      // existing flow with no ?mode= opens on the canvas, which is not what "make this one
      // yours" promised, and is unusable on the phone most of these links are opened on.
      setSearchParams((current) => {
        const next = without(current, 'template');
        next.set('mode', 'assistant');
        return next;
      }, { replace: true });
      if (!template) {
        // Unknown id, treat as a blank slate rather than keeping whatever
        // was on the canvas before. Happens legitimately during deploy skew
        // (landing referencing template ids the cached app bundle predates),
        // so leave a trace instead of failing silently.
        console.warn(`Unknown template id "${templateId}", opening a blank flow`);
        track('template_not_found', { templateId });
        setCurrentFlow(null);
        return;
      }

      // Save the template immediately so the user starts from a persisted flow,
      // unless the name is already taken in this environment (the Save dialog will
      // prompt for a new name) or saving is impossible (no WABA connection).
      const nameTaken = flows.some((f) => f.name === template.name);
      if (nameTaken || !isConnected) {
        setCurrentFlow({ name: template.name, flowJson: template.flowJson });
        track('template_used', { templateId, templateName: template.name, autoSaved: false });
        return;
      }
      track('template_used', { templateId, templateName: template.name, autoSaved: true });
      createFlow({
        name: template.name,
        description: template.description,
        flowJson: template.flowJson,
      })
        .then((saved) => {
          setCurrentFlow(saved);
          navigate(`/flow-builder/${saved.id}`, { replace: true });
        })
        .catch((err) => {
          // Auto-save is best-effort (e.g. demo flow limit reached), fall back
          // to the unsaved template; the user can still save via the dialog.
          console.warn('Template auto-save failed, keeping the flow unsaved:', err);
          setCurrentFlow({ name: template.name, flowJson: template.flowJson });
        });
      return;
    }

    if (isNewFlow) {
      // Once, like the template branch above. This effect re-runs whenever the environment's
      // flow list arrives (a new `flows` identity), and until the strip lands the URL still
      // says new=1, so a second run wiped whatever had been put on the canvas in between.
      // That is what left the worked example nameless: the flow was cleared out from under it
      // and the header fell back to the first screen's title (2026-09-19).
      if (handledNewRef.current) return;
      handledNewRef.current = true;
      setCurrentFlow(null);
      setSearchParams((current) => without(current, 'new'), { replace: true });
    }
  }, [flowId, templateId, isNewFlow, selectedEnvironment?.id, hasLoadedFlows, flows, isConnected, createFlow, loadFlowById, navigate, setCurrentFlow, setSearchParams]);

  useEffect(() => {
    track('builder_opened', { existingFlow: !!flowId, fromTemplate: !!templateId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlowSaved = (savedFlow) => {
    if (savedFlow?.id && !flowId) {
      // New flow was saved: move to its own URL, staying in the mode it was saved from. Dropping
      // ?mode= sent a flow saved in the assistant to the canvas, because a flow with an id and no
      // mode opens there (2026-09-29).
      const params = without(without(searchParams, 'new'), 'brief');
      params.set('mode', showAssistant ? 'assistant' : 'manual');
      navigate(`/flow-builder/${savedFlow.id}?${params.toString()}`, { replace: true });
    }
  };

  // Keep the active screen in the URL (?screen=) so refresh / link-sharing preserves context.
  const handleActiveScreenChange = useCallback((screenId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (screenId) next.set('screen', screenId);
      else next.delete('screen');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // On a phone the assistant is the builder; the manual one stays mounted but hidden, because
  // it owns the canvas the assistant reads the current flow from.
  const showAssistant = aiAvailable && (mode === 'ai' || !manualAllowed);
  const showManual = !showAssistant;

  // "Fix these with the assistant" from the manual builder's validation panel: switch to the
  // assistant and hand it the problems as the next message. Keyed so the pane sends each request
  // once, however many times it re-renders.
  const [assistantRequest, setAssistantRequest] = useState(null);
  const handleFixWithAssistant = useCallback((problems) => {
    if (!problems || problems.length === 0) return;
    setAssistantRequest({ id: Date.now(), problems });
    setMode('ai');
  }, [setMode]);

  // The brief from the landing page, handed to the assistant as its first message so the flow
  // is already being built when the page paints. One-shot: the ref absorbs the re-render the
  // strip causes, and the pane itself sends each request id exactly once.
  const handledBriefRef = useRef(false);
  useEffect(() => {
    if (!brief || handledBriefRef.current) return;
    handledBriefRef.current = true;
    const prompt = brief.slice(0, MAX_BRIEF);
    setSearchParams((current) => without(current, 'brief'), { replace: true });
    setAssistantRequest({ id: Date.now(), prompt });
    track('brief_arrived', { promptLength: prompt.length });
  }, [brief, setSearchParams]);

  // The worked example: put the flow on the canvas and the conversation in the chat, as though
  // it had just been built. Nothing is generated, this is the one flow we already know.
  const handledExampleRef = useRef(false);
  useEffect(() => {
    if (!example || handledExampleRef.current) return;
    handledExampleRef.current = true;
    setSearchParams((current) => {
      const next = without(current, 'example');
      next.set('mode', 'assistant');
      return next;
    }, { replace: true });
    // Deploy skew: a landing page newer than this bundle naming an example it does not have. A
    // blank builder is a poor welcome, so fall back to one that exists rather than show nothing.
    const shown = exampleFor(example) || exampleFor(DEFAULT_EXAMPLE_ID);
    if (!exampleFor(example)) {
      console.warn(`Unknown example "${example}", showing the one this build knows`);
    }
    setCurrentFlow({ name: shown.name, flowJson: shown.flowJson });
    setAssistantRequest({
      id: Date.now(),
      transcript: [
        { role: 'user', text: shown.brief },
        { role: 'assistant', text: shown.reply },
      ],
      flowJson: shown.flowJson,
      name: shown.name,
    });
    track('example_opened', { example: shown.id });
  }, [example, setCurrentFlow, setSearchParams]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {aiAvailable && manualAllowed && (
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(event, next) => next && setMode(next)}
            aria-label="Builder mode"
            // Full width on a phone so both halves are comfortably thumb-sized; 44px is the
            // floor, and the default small toggle is under it.
            sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiToggleButton-root': { minHeight: 44, flex: { xs: 1, sm: 'none' } } }}
          >
            <ToggleButton value="ai" aria-label="Assistant mode">
              <AutoAwesomeIcon fontSize="small" sx={{ mr: 0.75 }} />
              Assistant
            </ToggleButton>
            <ToggleButton value="manual" aria-label="Manual mode">
              <TuneIcon fontSize="small" sx={{ mr: 0.75 }} />
              Manual
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/*
        Both builders stay MOUNTED, hidden rather than unmounted, whichever mode is not currently
        shown. The manual builder owns the canvas the assistant reads from, so losing it would
        replace the user's work with an empty flow on the next chat message; the assistant owns
        the conversation (messages, sessionId, pending images), so losing IT would silently
        restart the chat every time someone glances at the canvas and back. `active` tells each
        one whether it is the one actually on screen, so only one of their two auto-save loops
        writes to the flow at a time.
      */}
      {/* Nothing else to offer: without generation configured, the manual builder is all there
          is, so it stays, with a word of warning rather than a silent bad time. */}
      {isPhone && !aiAvailable && (
        <Alert severity="info" square sx={{ borderRadius: 0 }}>
          Building by hand needs a wider screen. Open FlowGate on a computer to edit this flow.
        </Alert>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: showManual ? 'block' : 'none' }}>
        <FlowBuilder
          active={showManual}
          onFlowSaved={handleFlowSaved}
          initialScreenId={searchParams.get('screen')}
          onActiveScreenChange={handleActiveScreenChange}
          onScreensChange={handleScreensChange}
          onFixWithAssistant={aiAvailable ? handleFixWithAssistant : undefined}
        />
      </Box>

      {aiAvailable && (
        <Box sx={{ flex: 1, minHeight: 0, display: showAssistant ? 'block' : 'none' }}>
          <AiBuilderPane
            active={showAssistant}
            getCurrentFlowJson={currentFlowJson}
            onApply={applyGenerated}
            onFlowSaved={handleFlowSaved}
            request={assistantRequest}
            startBlank={startBlankRef.current}
            draftScope={flowId || 'new'}
          />
        </Box>
      )}
    </Box>
  );
};

export default FlowBuilderPage;
